import { Flag as FlagIcon } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { formatDate } from '../../format-date';
import { type Flag, resolveFlag, type Supplier, updateSupplier, type WriteOutcome } from './api';
import { Notice } from './Notice';

/**
 * «Segnalato» (board 3.5): each open report, with its reason, note, who and when. It stays until
 * Gestione resolves it: «Togli la segnalazione», or «Rendi non utilizzabile», which takes the
 * supplier out of the picker (its history stays) and resolves the report.
 */
export function FlagNotice({
  supplier,
  onWrite,
}: {
  readonly supplier: Supplier;
  readonly onWrite: (outcome: WriteOutcome, done: string) => void;
}) {
  const open = supplier.flags.filter((flag) => flag.resolvedAt === null);
  return (
    <>
      {open.map((flag) => (
        <OpenFlag key={flag.id} supplier={supplier} flag={flag} onWrite={onWrite} />
      ))}
    </>
  );
}

function OpenFlag({
  supplier,
  flag,
  onWrite,
}: {
  readonly supplier: Supplier;
  readonly flag: Flag;
  readonly onWrite: (outcome: WriteOutcome, done: string) => void;
}) {
  const { t } = useTranslation('suppliers');
  const [busy, setBusy] = useState(false);
  const canSetAside = supplier.can.setStatus && supplier.status === 'active';
  const by = flag.raisedBy ?? t('flag.someone');

  async function run(write: () => Promise<WriteOutcome>, done: string) {
    if (busy) return;
    setBusy(true);
    const outcome = await write();
    setBusy(false);
    onWrite(outcome, done);
  }

  /** The status first, then the report; a refused resolve still shows the supplier as it now is. */
  async function setAside() {
    if (busy) return;
    setBusy(true);
    const changed = await updateSupplier(supplier, { status: 'unusable' });
    const resolved = changed.ok ? await resolveFlag(supplier.id, flag.id) : changed;
    setBusy(false);
    if (resolved.ok || !changed.ok) onWrite(resolved, t('flag.setAside'));
    else onWrite(changed, t('flag.madeUnusable'));
  }

  return (
    <Notice
      tone="error"
      icon={FlagIcon}
      title={t('flag.title', { reason: t(`flag.reason.${flag.reason}`) })}
      actions={
        supplier.can.resolveFlags ? (
          <>
            {canSetAside ? (
              <Button size="sm" variant="outline" disabled={busy} onClick={() => void setAside()}>
                {t('flag.makeUnusable')}
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              disabled={busy}
              onClick={() => void run(() => resolveFlag(supplier.id, flag.id), t('flag.resolved'))}
            >
              {t('flag.resolve')}
            </Button>
          </>
        ) : null
      }
    >
      <p>
        {flag.note ? `«${flag.note}» — ` : ''}
        {by}, {formatDate(flag.raisedAt)}
      </p>
    </Notice>
  );
}
